/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ClearTelemetry, ClearTelemetryData, ClearTelemetryResponseSchema } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type ClearTelemetryMutationConfig = {
  onSuccess?: (data: ClearTelemetryResponseSchema, variables: ClearTelemetryParams) => void;
  onError?: (error: unknown, variables: ClearTelemetryParams) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for clearing telemetry events by mimic IDs or telemetry event IDs
 * through the management plane API.
 *
 * @param config.onSuccess - Called after successful clearing of telemetry events
 * @param config.onError - Called if clearing fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for clearing telemetry events
 *
 * @example
 * ```tsx
 * const { mutate } = useClearTelemetryMutation({
 *   onSuccess: (data) => console.log('Telemetry events cleared:', data),
 *   onError: (error) => console.error('Clearing telemetry events failed:', error)
 * });
 *
 * // Clear Telemetry Events
 * mutate({
 *   reason: 'some reason here',
 *   telemetryType: 'hallmarks',
 *   telemetryEvents: [],
 *   mimicIds: [],
 * });
 * ```
 */

export type ClearTelemetryParams = ClearTelemetry & {
  telemetryType: ClearTelemetryData['telemetryType'];
};

export function useClearTelemetryMutation(nodeId: string, config?: ClearTelemetryMutationConfig) {
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return useMutation<ClearTelemetryResponseSchema, unknown, ClearTelemetryParams>({
    mutationFn: (form: ClearTelemetryParams) => {
      switch (form.type) {
        case 'selected': {
          return mgmtPlaneAPIClient.nodes.clearTelemetry({
            tenantId: tenantID!,
            nodeId,
            telemetryType: form.telemetryType,
            requestBody: {
              type: 'selected',
              reason: form.reason,
              telemetryEventIds: form.telemetryEventIds,
              mimicIds: form.mimicIds,
            },
          });
        }
        case 'all': {
          return mgmtPlaneAPIClient.nodes.clearTelemetry({
            tenantId: tenantID!,
            nodeId,
            telemetryType: form.telemetryType,
            requestBody: {
              type: 'all',
              reason: form.reason,
            },
          });
        }
        default:
          throw new Error('Invalid telemetry type');
      }
    },
    onSuccess: config?.onSuccess,
    onError: config?.onError,
    onSettled: config?.onSettled,
  });
}
